import { Link } from 'react-router-dom';
import { useState } from 'react';

export default function Footer() {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleNewsletterSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      formBody.append('email', email);

      const response = await fetch('https://readdy.ai/api/form/d5k5n442ojajeau494g0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setEmail('');
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const socialLinks = [
    { icon: 'ri-linkedin-fill', url: 'https://www.linkedin.com/in/muhammed-ademola-73b264369', label: 'LinkedIn' },
    { icon: 'ri-instagram-line', url: 'https://www.instagram.com/keystone_strategy_advisory/', label: 'Instagram' },
    { icon: 'ri-pinterest-fill', url: 'https://www.pinterest.com/keystone_strategy_advisory/', label: 'Pinterest' },
    { icon: 'ri-twitter-x-line', url: 'https://x.com/Muh_ademola', label: 'X' },
    { icon: 'fa-brands fa-tiktok', url: 'https://www.tiktok.com/@muh_ademola', label: 'TikTok' },
    { icon: 'fa-brands fa-threads', url: 'https://www.threads.com/@keystone_strategy_advisory', label: 'Threads' },
    { type: 'image', url: 'https://www.upwork.com/freelancers/~01c5e417b0fc82a5a2', label: 'Upwork', image: 'https://public.readdy.ai/ai/img_res/63af44e9bb8c30e30c9ddaadc953ab2c.jpg' }
  ];

  return (
    <footer className="bg-[#0F2438] rounded-t-[40px] mx-5 mt-20 relative overflow-hidden">
      {/* Watermark */}
      <div className="absolute bottom-0 left-0 right-0 text-center opacity-[0.03] pointer-events-none">
        <div className="text-[120px] lg:text-[180px] font-bold text-white tracking-wider">
          KEYSTONE
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-16 relative z-10">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-12 mb-12">
          {/* Column 1: Brand & Mission */}
          <div className="space-y-6">
            <img 
              src="https://static.readdy.ai/image/d4dd615c93e49848557a049e3ea85ee4/9a9282d1052f6d3b271c57a650dbbdb7.png" 
              alt="Keystone Strategy Advisory" 
              className="h-12 w-auto brightness-0 invert"
            />
            <p className="text-sm text-white/70 leading-relaxed">
              Your Partner in US Business Compliance
            </p>
          </div>

          {/* Column 2: Services */}
          <div>
            <h3 className="text-xs uppercase font-semibold text-white mb-6 tracking-wider">
              SERVICES
            </h3>
            <ul className="space-y-4">
              {[
                'LLC Formation',
                'EIN Application',
                'Operating Agreements',
                'Compliance Support',
                'Tax Guidance',
                'Banking Setup'
              ].map((item, index) => (
                <li key={index}>
                  <Link 
                    to="/services" 
                    className="text-sm text-white/80 hover:text-white transition-colors cursor-pointer"
                  >
                    {item}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Column 3: Company */}
          <div>
            <h3 className="text-xs uppercase font-semibold text-white mb-6 tracking-wider">
              COMPANY
            </h3>
            <ul className="space-y-4">
              <li>
                <Link 
                  to="/about" 
                  className="text-sm text-white/80 hover:text-white transition-colors cursor-pointer"
                >
                  About Us
                </Link>
              </li>
              <li>
                <Link 
                  to="/" 
                  className="text-sm text-white/80 hover:text-white transition-colors cursor-pointer"
                >
                  Client Reviews
                </Link>
              </li>
              <li>
                <Link 
                  to="/contact" 
                  className="text-sm text-white/80 hover:text-white transition-colors cursor-pointer"
                >
                  Contact
                </Link>
              </li>
              <li>
                <Link 
                  to="/" 
                  className="text-sm text-white/80 hover:text-white transition-colors cursor-pointer"
                >
                  Privacy Policy
                </Link>
              </li>
            </ul>
          </div>

          {/* Column 4: Newsletter */}
          <div>
            <h3 className="text-xs uppercase font-semibold text-white mb-6 tracking-wider">
              STAY INFORMED
            </h3>
            <p className="text-sm text-white/70 mb-4">
              Get compliance updates and business tips
            </p>
            <form data-readdy-form onSubmit={handleNewsletterSubmit} className="flex">
              <input
                type="email"
                name="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Your email"
                required
                disabled={isSubmitting}
                className="flex-1 bg-[#1B3A5F] text-white placeholder-white/50 px-4 py-3 rounded-l-full text-sm focus:outline-none focus:ring-2 focus:ring-[#C9A961] disabled:opacity-50"
              />
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-11 h-11 bg-[#C9A961] rounded-full flex items-center justify-center text-white hover:bg-[#B89851] transition-colors flex-shrink-0 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
                aria-label="Subscribe"
              >
                <i className="ri-arrow-right-line text-lg"></i>
              </button>
            </form>
            {submitStatus === 'success' && (
              <p className="text-xs text-green-400 mt-2">Thank you for subscribing!</p>
            )}
            {submitStatus === 'error' && (
              <p className="text-xs text-red-400 mt-2">Something went wrong. Please try again.</p>
            )}
            <p className="text-sm text-white/70 mt-6">
              You can also hire us on{' '}
              <a 
                href="https://www.upwork.com/freelancers/~01c5e417b0fc82a5a2" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-[#C9A961] hover:text-white transition-colors cursor-pointer font-semibold"
              >
                Upwork
              </a>
              .
            </p>
          </div>
        </div>

        {/* Bottom Section */}
        <div className="border-t border-white/15 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <p className="text-sm text-white/60">
              © 2025 Keystone Strategy Advisory. All rights reserved.
            </p>
            
            <div className="flex items-center gap-5">
              {socialLinks.map((social, index) => (
                <a
                  key={index}
                  href={social.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-6 h-6 flex items-center justify-center text-white/80 hover:text-[#C9A961] transition-colors cursor-pointer"
                  aria-label={social.label}
                >
                  {social.type === 'image' ? (
                    <img 
                      src={social.image} 
                      alt={social.label}
                      className="w-full h-full object-contain brightness-0 invert opacity-80 hover:opacity-100 transition-opacity"
                    />
                  ) : (
                    <i className={`${social.icon} text-lg`}></i>
                  )}
                </a>
              ))}
            </div>
          </div>
          
          <div className="text-center mt-6">
            <a 
              href="https://readdy.ai/?ref=logo" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-xs text-white/40 hover:text-white/60 transition-colors cursor-pointer"
            >
              Powered by Readdy
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}